import React, { useMemo } from 'react';
import { BsThreeDots } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Link, useRouteMatch } from 'react-router-dom';
import { Box, Flex, Grid, GridItem, Icon, Image, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import IconButtonMenu from '../../../../components/IconButtonMenu';
import IntegrationExternalIdStack from '../../../../components/IntegrationExternalIdStack';
import RowGridColumn from '../../../../components/Row/components/RowGridColumn';
import StatusBadge from '../../../../components/StatusBadge';
import { JobProfilePage } from '../../../../constants/duskIds';
import * as Entities from '../../../../constants/entities';
import { Statuses } from '../../../../constants/statuses';
import { selectCurrentJob, selectCurrentlyUpdatingJob } from '../../../../redux/modules/jobs';
import { selectUser } from '../../../../redux/modules/user';
import colors from '../../../../styles/theme/foundation/colors';
import { getProjectStatusFromRequirementStatus, getStatusFilterSelectOptions } from '../../../../utils/statusUtil';
import RecordWatcherIconButtonContainer from '../../../V2InsuredProfilePage/components/Records/RecordsBody/RecordWatcherIconButtonContainer';
import Row from '../Row';

import { activeMenuOptions, archivedMenuOptions } from './util';

const InsuredRow = ({ insured, viewOnly }) => {
  const { url } = useRouteMatch();
  const { companyId } = useParams();
  const jobUpdating = useSelector(selectCurrentlyUpdatingJob);
  const currentUser = useSelector(selectUser);
  const currentJob = useSelector(selectCurrentJob);

  const insuredStatus = getStatusFilterSelectOptions(Entities.Insured)
    .find(status => status.value === getProjectStatusFromRequirementStatus(insured.status)) || Statuses.NotApproved;

  const recordArchived = insuredStatus.value === Statuses.Archived;
  const insuredArchived = insured.overallStatus === Statuses.Archived;

  const recordIntegrations = useMemo(() => insured?.recordIntegrations ?? [], [insured]);

  const getMenuOptions = () => {
    if (viewOnly) return [];

    if (insuredArchived) {
      return archivedMenuOptions.filter(o => o.id === 'deleteInsured');
    }

    if (recordArchived) {
      return archivedMenuOptions;
    }

    return activeMenuOptions(insured, companyId, currentUser);
  };

  const menuOptions = getMenuOptions();

  return (
    <Link
      rel="noreferrer"
      style={{
        textDecoration: 'none',
        _hover: { textDecoration: 'none' },
        ...(viewOnly && { pointerEvents: 'none' }),
        ...(insuredArchived && { cursor: 'not-allowed' })
      }}
      target={!insuredArchived && '_blank'}
      to={insuredArchived ? '#' : url.replace(/projects.*/, `insureds/${insured.id}/records/${insured.requirementId}`)}
    >
      <Tooltip label={insuredArchived ? 'This insured is archived' : ''}>
        <Box>
          <Row
            clickable={!viewOnly && !insuredArchived}
            height="82px"
            layerStyle={recordArchived ? 'secondaryArchived' : 'row'}
          >
            <Grid
              alignItems="center"
              gap={4}
              height="100%"
              justifyContent="center"
              templateColumns="repeat(20, 1fr)"
              textAlign="left"
              textStyle="bodyMd"
            >
              <GridItem colSpan={6} height="100%">
                <RowGridColumn align="flex-start" label="Insured Name">
                  <Tooltip label={insured?.externalId}>
                    <Text
                      data-cy={insured.cypressId}
                      dusk={JobProfilePage.getRecordRowByName(insured.companyName)}
                      fontWeight="semibold"
                      noOfLines={2}
                      textAlign="left"
                    >
                      {insured.companyName}
                    </Text>
                  </Tooltip>
                </RowGridColumn>
              </GridItem>

              <GridItem colSpan={4} height="100%">
                <RowGridColumn align="flex-start" label="Record #">
                  <Tooltip hasArrow={false} label={insured.contractNumber ?? 'No record number'}>
                    <Text fontWeight="medium" noOfLines={1} textAlign="left">{insured.contractNumber || '-'}</Text>
                  </Tooltip>
                </RowGridColumn>
              </GridItem>

              <GridItem
                colSpan={3}
                height="100%"
              >
                <Text
                  color={colors.greyScale['700']}
                  mb={1}
                  position="relative"
                  textAlign="left"
                  textStyle="bodySm500"
                >
                  Record External Ids
                </Text>
                <IntegrationExternalIdStack
                  integrations={recordIntegrations}
                  resourceName="recordId"
                />
              </GridItem>

              <GridItem colSpan={3} height="100%" margin="auto">
                <RowGridColumn label="Watchers">
                  <RecordWatcherIconButtonContainer
                    stopPropagation
                    companyId={companyId}
                    currentUser={currentUser}
                    departmentId={currentJob?.departmentId}
                    insuredId={insured.id}
                    recordId={insured.requirementId}
                  />
                </RowGridColumn>
              </GridItem>

              <GridItem colSpan={3} height="100%">
                <Flex direction="column" height="100%" justify="center" width="100%">
                  <StatusBadge
                    duskSelector={JobProfilePage.getRecordStatusBadge(insured.requirementId)}
                    minW="104px"
                    size="sm"
                    status={insured.status}
                  />
                </Flex>
              </GridItem>

              <GridItem colSpan={1} height="100%">
                {!viewOnly && (
                <Tooltip label="Record Options" placement="top">
                  <Flex align="flex-end" direction="column" height="100%" justify="center">
                    <IconButtonMenu
                      ariaLabel="Record Status Menu"
                      iconElement={<Icon as={BsThreeDots} boxSize="20px" px={0} />}
                      id={insured.requirementId}
                      isLoading={jobUpdating}
                      menuOptions={menuOptions}
                      size="sm"
                      width="auto"
                    />
                  </Flex>
                </Tooltip>
                )}
              </GridItem>
            </Grid>

          </Row>
        </Box>
      </Tooltip>
    </Link>
  );
};

InsuredRow.propTypes = {
  insured: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    companyName: PropTypes.string,
    contractNumber: PropTypes.string,
    email: PropTypes.string,
    projectId: PropTypes.number,
    requirementId: PropTypes.number,
    externalId: PropTypes.string,
    overallStatus: PropTypes.string,
    cypressId: PropTypes.string
  }),
  viewOnly: PropTypes.bool
};

export default InsuredRow;
